import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Account } from 'modules/shared/models/account.models';
import { AdminConfig } from 'modules/shared/models/admin-config.models';
import { Role } from 'modules/shared/models/role.models';
import {
  AccountListResponse, AccountPayload, UpdateRoleRequest, AccountBatchUploadPayload,
  RoleResponse, CreateAccountPayload, GroupPermissionResponse, ConfigPayload, UnlockUserPayload
} from 'store/models/admin.models';
import { BaseListReqParams } from 'modules/shared/models/base.models';
import { Domain } from 'modules/shared/models/domain.models';

import { environment } from 'environments/environment';

import { ApiAbstractService } from './api-abstract.service';

@Injectable()
export class AdminService extends ApiAbstractService {
  readonly apiBase: string = environment.API_BASE;

  constructor(private httpClient: HttpClient) {
    super(environment.API_BASE);
  }

  getDomains(): Observable<Domain[]> {
    return this.httpClient.get<Domain[]>(this.buildUrl(`/api/domains/`, {limit: 999, offset: 0})).pipe(
      map((res: any) => res.results)
    );
  }

  getAccounts(payload: AccountPayload): Observable<AccountListResponse> {
    return this.httpClient.get<AccountListResponse>(this.buildUrl(`/api/users/`, payload));
  }

  createAccount(payload: CreateAccountPayload): Observable<Account> {
    const url = `${this.apiBase}/api/users/`;
    const body = {
      name: payload.name,
      email: payload.email,
      roles: payload.roles
    };
    return this.httpClient.post<Account>(url, body);
  }

  getAuthRoles(): Observable<RoleResponse> {
    const url = `${this.apiBase}/api/auth_roles/`;
    return this.httpClient.get<RoleResponse>(url);
  }

  updateRoles(request: UpdateRoleRequest): Observable<Role[]> {
    const url = `${this.apiBase}/api/users/${request.oauthId}/roles/`;
    const body = {
      roles: request.roles
    };
    return this.httpClient.patch<Role[]>(url, body);
  }

  accountBatchUpload(payload: AccountBatchUploadPayload): Observable<Account[]> {
    const url = `${this.apiBase}/api/users/bulk_create/`;
    const formData: FormData = new FormData();
    formData.append('file', payload.file);
    return this.httpClient.post<Account[]>(url, formData);
  }

  getPermissions(payload: BaseListReqParams): Observable<GroupPermissionResponse> {
    let url = `${this.apiBase}/api/permissions/?`;
    url += payload.limit ? `limit=${payload.limit}` : '';
    url += payload.offset ? `&offset=${payload.offset}` : '';
    url += payload.sort ? `&sort=${payload.sort}` : '';
    return this.httpClient.get<GroupPermissionResponse>(url);
  }

  getConfig(key: string): Observable<AdminConfig> {
    const url = `${this.apiBase}/api/appconfig/${key}/`;
    return this.httpClient.get<AdminConfig>(url);
  }

  updateConfig(payload: ConfigPayload): Observable<AdminConfig> {
    const url = `${this.apiBase}/api/appconfig/${payload.key}/`;
    const body = payload.value ? {
      key: payload.key,
      value: payload.value,
    } : {
      key: payload.key,
      jsonValue: payload.jsonValue,
    };

    return this.httpClient.put<AdminConfig>(url, body);
  }

  unlockUser(payload: UnlockUserPayload): Observable<Account> {
    const url = `${this.apiBase}/api/users/${payload.oauthId}/unlock`;
    return this.httpClient.patch<Account>(url, null);
  }

  checkLastLogin(oAuthId: string): Observable<any> {
    const url = `${this.apiBase}/api/users/${oAuthId}/last_login`;
    return this.httpClient.get<any>(url);
  }
}
